/**
 * «Liquidación» de un colaborador (administración, 4/11/2026): mes desde y
 * mes hasta (por defecto, el mes en curso), «Calcular» y la tabla con sus
 * actos, el % aplicado a cada uno y de dónde sale, los totales por mes y del
 * periodo (pendiente y pagado), y «Exportar a Excel».
 *
 * La calcula el servidor (GET /api/colaboradores/liquidacion) con el % con
 * que se REGISTRÓ cada acto; si alguno hoy tocaría otro, lo avisa sin
 * cambiarlo. Gestión ve la de cualquiera; un médico, solo la suya (el
 * servidor no le deja otra; también al anestesista al que se le apuntan
 * actos). Al cambiar cualquiera de los dos meses se borra el resultado: lo
 * que se ve siempre es del periodo que dicen las casillas.
 *
 * Props: { medicoId, esGestion }
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Calculator, Download, Loader2 } from 'lucide-react';
import { api, mensajeError } from '../../lib/api';
import { textoPorcentaje } from '../../lib/importeEuros';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const eur = (v) => (v === null || v === undefined ? '—'
  : Number(v).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €');

/** El mes en curso, «aaaa-mm», en la hora de este equipo. */
export function mesActual(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** «2099-03» → «03/2099». */
const textoMes = (m) => (/^\d{4}-\d{2}$/.test(m || '') ? `${m.slice(5, 7)}/${m.slice(0, 4)}` : (m || ''));
const fechaLegible = (s) => (/^\d{4}-\d{2}-\d{2}$/.test(s || '') ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}` : (s || ''));

/** El nombre del fichero que manda el servidor (o uno sin datos de pacientes). */
function nombreDelFichero(cabeceras, porDefecto) {
  const cd = (cabeceras && (cabeceras['content-disposition'] || cabeceras['Content-Disposition'])) || '';
  const m = /filename="([^"]+)"/.exec(cd);
  return m ? m[1] : porDefecto;
}

export default function LiquidacionColaborador({ medicoId, esGestion }) {
  const [desde, setDesde] = useState(mesActual());
  const [hasta, setHasta] = useState(mesActual());
  const [liq, setLiq] = useState(null);
  const [error, setError] = useState('');
  const [calculando, setCalculando] = useState(false);
  const [exportando, setExportando] = useState(false);

  const parametros = () => {
    const p = { desde_mes: desde, hasta_mes: hasta || desde };
    if (esGestion && medicoId) p.medico_id = medicoId;
    return p;
  };
  const valido = () => {
    if (!/^\d{4}-\d{2}$/.test(desde) || (hasta && !/^\d{4}-\d{2}$/.test(hasta))) {
      setError('Elige el mes «desde» y el mes «hasta».'); return false;
    }
    if (hasta && desde > hasta) { setError('El mes «desde» no puede ser posterior al mes «hasta».'); return false; }
    return true;
  };

  const calcular = async () => {
    if (!valido()) return;
    setError(''); setCalculando(true);
    try {
      const r = await api.get('/api/colaboradores/liquidacion', { params: parametros() });
      setLiq(r.data);
    } catch (e) { setError(mensajeError(e, 'No se pudo calcular la liquidación')); setLiq(null); }
    finally { setCalculando(false); }
  };

  const exportar = async () => {
    if (!valido()) return;
    setError(''); setExportando(true);
    try {
      const p = parametros();
      const r = await api.get('/api/colaboradores/liquidacion.xlsx', { params: p, responseType: 'blob' });
      const blob = r.data instanceof Blob ? r.data : new Blob([r.data],
        { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = nombreDelFichero(r.headers, `liquidacion-${p.desde_mes}_${p.hasta_mes}.xlsx`);
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(mensajeError(e, 'No se pudo exportar a Excel')); }
    finally { setExportando(false); }
  };

  const tot = liq?.totales || {};
  return (
    <Card className="cjo-card p-4 space-y-3" data-testid="colab-liquidacion">
      <h3 className="text-sm font-semibold text-[#13231E]">Liquidación</h3>
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <Label htmlFor="colab-liq-desde">Mes desde</Label>
          <Input id="colab-liq-desde" type="month" value={desde} className="w-44"
            onChange={(e) => { setError(''); setLiq(null); setDesde(e.target.value); }} data-testid="colab-liq-desde" />
        </div>
        <div>
          <Label htmlFor="colab-liq-hasta">Mes hasta</Label>
          <Input id="colab-liq-hasta" type="month" value={hasta} className="w-44"
            onChange={(e) => { setError(''); setLiq(null); setHasta(e.target.value); }} data-testid="colab-liq-hasta" />
        </div>
        <Button type="button" size="sm" onClick={calcular} disabled={calculando}
          className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="colab-liq-calcular">
          {calculando ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Calculator className="w-4 h-4 mr-1.5" />}
          Calcular
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={exportar} disabled={exportando}
          data-testid="colab-liq-exportar">
          {exportando ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />}
          Exportar a Excel
        </Button>
      </div>
      {error && <p className="text-sm text-red-700" role="alert" data-testid="colab-liq-error">{error}</p>}

      {liq && (
        <>
          {liq.aviso_pct_distinto && (
            <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-950"
              role="status" data-testid="colab-liq-aviso">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
              <span>{liq.aviso_pct_distinto.texto}</span>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[860px]" data-testid="colab-liq-tabla">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-muted-foreground text-left border-b border-border">
                  <th className="py-2 px-2">Fecha</th>
                  <th className="py-2 px-2">Paciente</th>
                  <th className="py-2 px-2">Servicio</th>
                  <th className="py-2 px-2 text-right">Base</th>
                  <th className="py-2 px-2 text-right">%</th>
                  <th className="py-2 px-2">De dónde sale</th>
                  <th className="py-2 px-2 text-right">Médico</th>
                  <th className="py-2 px-2 text-right">Clínica</th>
                  <th className="py-2 px-2 text-center">Pagado</th>
                </tr>
              </thead>
              <tbody>
                {(liq.filas || []).map((f) => (
                  <tr key={f.id} className="border-b border-border/60" data-testid={`colab-liq-fila-${f.id}`}>
                    <td className="py-1.5 px-2 tabular-nums whitespace-nowrap">{fechaLegible(f.fecha)}</td>
                    <td className="py-1.5 px-2">{f.paciente_nombre}</td>
                    <td className="py-1.5 px-2">
                      {f.servicio} <span className="text-xs text-muted-foreground">· {f.tipo === 'cirugia' ? 'cirugía' : 'tratamiento'}</span>
                    </td>
                    <td className="py-1.5 px-2 text-right tabular-nums">{eur(f.base)}</td>
                    <td className="py-1.5 px-2 text-right tabular-nums">{f.pct_medico === null || f.pct_medico === undefined ? '—' : textoPorcentaje(f.pct_medico)}</td>
                    <td className="py-1.5 px-2 text-xs text-muted-foreground">{f.origen_texto}</td>
                    <td className="py-1.5 px-2 text-right tabular-nums font-semibold text-[#1E4A3F]">{eur(f.parte_medico)}</td>
                    <td className="py-1.5 px-2 text-right tabular-nums">{eur(f.parte_clinica)}</td>
                    <td className="py-1.5 px-2 text-center">{f.pagado ? 'Sí' : 'No'}</td>
                  </tr>
                ))}
                {(liq.filas || []).length === 0 && (
                  <tr><td colSpan={9} className="py-4 text-center text-sm text-muted-foreground" data-testid="colab-liq-vacio">
                    Sin actos en ese periodo.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>

          {(liq.meses || []).length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]" data-testid="colab-liq-meses">
                <thead>
                  <tr className="text-[11px] uppercase tracking-wider text-muted-foreground text-left border-b border-border">
                    <th className="py-2 px-2">Mes</th>
                    <th className="py-2 px-2 text-right">Actos</th>
                    <th className="py-2 px-2 text-right">Base</th>
                    <th className="py-2 px-2 text-right">Médico</th>
                    <th className="py-2 px-2 text-right">Clínica</th>
                    <th className="py-2 px-2 text-right">Pendiente</th>
                    <th className="py-2 px-2 text-right">Pagado</th>
                  </tr>
                </thead>
                <tbody>
                  {liq.meses.map((m) => (
                    <tr key={m.mes} className="border-b border-border/60" data-testid={`colab-liq-mes-${m.mes}`}>
                      <td className="py-1.5 px-2 tabular-nums">{textoMes(m.mes)}</td>
                      <td className="py-1.5 px-2 text-right tabular-nums">{m.actos}</td>
                      <td className="py-1.5 px-2 text-right tabular-nums">{eur(m.base)}</td>
                      <td className="py-1.5 px-2 text-right tabular-nums">{eur(m.parte_medico)}</td>
                      <td className="py-1.5 px-2 text-right tabular-nums">{eur(m.parte_clinica)}</td>
                      <td className="py-1.5 px-2 text-right tabular-nums text-[#8a5a12]">{eur(m.pendiente)}</td>
                      <td className="py-1.5 px-2 text-right tabular-nums">{eur(m.pagado)}</td>
                    </tr>
                  ))}
                  <tr className="font-semibold" data-testid="colab-liq-total">
                    <td className="py-1.5 px-2">Total</td>
                    <td className="py-1.5 px-2 text-right tabular-nums">{tot.actos}</td>
                    <td className="py-1.5 px-2 text-right tabular-nums">{eur(tot.base)}</td>
                    <td className="py-1.5 px-2 text-right tabular-nums">{eur(tot.parte_medico)}</td>
                    <td className="py-1.5 px-2 text-right tabular-nums">{eur(tot.parte_clinica)}</td>
                    <td className="py-1.5 px-2 text-right tabular-nums text-[#8a5a12]">{eur(tot.pendiente)}</td>
                    <td className="py-1.5 px-2 text-right tabular-nums">{eur(tot.pagado)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Card>
  );
}
